// CustomMenu.js — Render del menú personalizado configurado en el admin
// (config_json.menu_config). Inline en escritorio; hamburguesa ≤991px.
// Los ítems pueden llevar `hijos: []` (submenú): en escritorio se despliega al
// pasar el mouse / hacer clic, y en móvil se abre como acordeón dentro del panel.
import React, { useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIdioma } from '../../contexts/IdiomaContext';
import { BACKEND_BASE } from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';
import useEntregasPendientes, { esTutor, textoMenuEntregas } from '../entregas/useEntregasPendientes';

const Wrap = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: ${p => p.$pos === 'izquierda' ? 'flex-start' : 'flex-end'};
`;

const DesktopList = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.$sep ? '0' : '0.4rem'};
  ${p => p.$bg ? `background: ${p.$bg}; padding: ${p.$pad || '0.3rem 0.7rem'}; border-radius: ${p.$radius || '12px'};` : ''}
  /* Separador vertical entre ítems (píldora tipo "Inicio | Webinar | ...").
     Los ítems con borde propio (data-borde) quedan fuera: su configuración
     individual manda sobre el separador general. */
  ${p => p.$sep ? `
    & > *:not(:last-child):not([data-borde]) {
      border-right: 1px solid ${p.$sep};
      border-radius: 0;
    }
  ` : ''}
  @media (max-width: 991px) { display: none; }
`;

const Burger = styled.button`
  display: none;
  @media (max-width: 991px) { display: inline-flex; }
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  color: ${p => p.$color || '#0f355b'};
  overflow: hidden;
`;

const Drawer = styled.div`
  display: none;
  @media (max-width: 991px) {
    display: ${p => p.$open ? 'flex' : 'none'};
    flex-direction: column;
    gap: 0.2rem;
    position: absolute;
    top: 100%;
    ${p => p.$pos === 'izquierda' ? 'left: 0;' : 'right: 0;'}
    min-width: ${p => p.$ancho || '200px'};
    background: ${p => p.$bg || '#fff'};
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(15,53,91,0.15);
    padding: 0.5rem;
    z-index: 1200;
  }
`;

const ItemBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${p => p.$iconPos === 'right' ? 'row-reverse' : p.$iconPos === 'top' ? 'column' : 'row'};
  gap: 0.4rem;
  background: ${p => p.$bg || 'transparent'};
  color: ${p => p.$color || '#0f355b'};
  border: none;
  /* Borde por ítem: se pinta solo el lado pedido (izquierda/derecha). Va
     después del border:none para ganarle, y el radius se anula de ese lado
     para que la línea salga recta. */
  ${p => p.$bordeLado === 'izquierda' && `border-left: ${p.$bordeAncho || 1}px solid ${p.$bordeColor || 'currentColor'};`}
  ${p => p.$bordeLado === 'derecha' && `border-right: ${p.$bordeAncho || 1}px solid ${p.$bordeColor || 'currentColor'};`}
  border-radius: 8px;
  ${p => p.$bordeLado === 'izquierda' && 'border-top-left-radius: 0; border-bottom-left-radius: 0;'}
  ${p => p.$bordeLado === 'derecha' && 'border-top-right-radius: 0; border-bottom-right-radius: 0;'}
  padding: 0.45rem 0.85rem;
  font-weight: ${p => p.$peso || 600};
  font-size: ${p => p.$size || '0.9rem'};
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s, background 0.2s, color 0.2s;
  font-family: ${p => p.$font || 'inherit'};
  white-space: nowrap;
  /* Hover configurable desde el admin: si no se define color/fondo de hover,
     queda el comportamiento de siempre (leve opacidad + fondo gris). */
  &:hover {
    opacity: ${p => (p.$hoverColor ? 1 : 0.85)};
    background: ${p => p.$hoverBg || p.$bg || 'rgba(0,0,0,0.05)'};
    ${p => p.$hoverColor && `color: ${p.$hoverColor};`}
  }
  @media (max-width: 991px) {
    width: 100%;
    justify-content: flex-start;
    /* En el drawer los ítems van apilados en vertical: un borde lateral se ve
       fuera de lugar, así que se quita junto con el radius recortado. */
    border: none;
    border-radius: 8px;
    ${p => p.$movilSize ? `font-size: ${p.$movilSize};` : ''}
    ${p => p.$movilColor ? `color: ${p.$movilColor};` : ''}
  }
  img { object-fit: contain; }
`;

// --- Submenú (escritorio) -------------------------------------------------
// Contenedor relativo del ítem padre. Se le pasa data-borde para que la regla
// de separadores de DesktopList lo trate igual que a un ItemBtn con borde.
const SubWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  @media (max-width: 991px) { width: 100%; display: block; }
`;

const Dropdown = styled.div`
  display: ${p => p.$open ? 'flex' : 'none'};
  flex-direction: column;
  gap: 2px;
  position: absolute;
  top: 100%;
  ${p => p.$pos === 'izquierda' ? 'left: 0;' : 'right: 0;'}
  margin-top: 6px;
  min-width: 210px;
  background: ${p => p.$bg || '#ffffff'};
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15,53,91,0.18);
  padding: 0.4rem;
  z-index: 1300;
  /* Puente invisible para que el hover no se corte entre el botón y el panel */
  &::before { content: ''; position: absolute; top: -8px; left: 0; right: 0; height: 8px; }
  @media (max-width: 991px) { display: none; }
`;

const SubItemBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  background: transparent;
  color: ${p => p.$color || '#0f355b'};
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  font-weight: 600;
  font-size: ${p => p.$size || '0.88rem'};
  font-family: ${p => p.$font || 'inherit'};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover {
    background: ${p => p.$hoverBg || 'rgba(15,53,91,0.08)'};
    ${p => p.$hoverColor && `color: ${p.$hoverColor};`}
  }
  img { object-fit: contain; }
`;

// --- Submenú (móvil, dentro del drawer) -----------------------------------
const MobileRow = styled.div`
  display: none;
  @media (max-width: 991px) { display: flex; align-items: center; width: 100%; }
`;

const Caret = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.45rem 0.6rem;
  font-size: 0.75rem;
  line-height: 1;
  color: ${p => p.$color || '#0f355b'};
  transition: transform 0.2s;
  transform: rotate(${p => p.$open ? '180deg' : '0deg'});
`;

const MobileSubList = styled.div`
  display: none;
  @media (max-width: 991px) {
    display: ${p => p.$open ? 'flex' : 'none'};
    flex-direction: column;
    gap: 0.15rem;
    padding-left: 0.9rem;
    margin: 0.15rem 0 0.35rem;
    border-left: 2px solid rgba(255,255,255,0.25);
  }
`;

const CustomMenu = ({ items, posicion = 'derecha', user, accent = '#0f355b', fontSize, pesoMenu, movilBg, movilAncho, movilFontSize, movilColor, colorOverride, itemColors, bgMenu, radiusMenu, padMenu, sepMenu, fontMenu, subBg, subColor, hoverColor, hoverBg, activoBg, activoColor, onLogin, onLogout, onRegister }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { idioma } = useIdioma();
  const [open, setOpen] = useState(false);
  const [subAbierto, setSubAbierto] = useState(null);   // submenú abierto en escritorio
  const [subMovil, setSubMovil] = useState(null);       // submenú abierto en el drawer
  const { proyecto } = useProject();
  const { pendientes: entregasPendientes } = useEntregasPendientes(esTutor(user) ? user : null);

  // Reglas de visibilidad comunes a ítems y sub-ítems.
  const esVisible = (it) => {
    if (it.solo_logueado && !user) return false;
    if (it.solo_invitado && user) return false;
    if (it.tipo === 'pagina' && !it.pagina_slug) return false;
    // País: si el ítem está restringido a países y conocemos el país del
    // usuario y no coincide → ocultar. Si no conocemos el país (invitado) se
    // muestra, igual que en el filtrado de páginas (mis-paginas.php).
    if (Array.isArray(it.paises) && it.paises.length && user?.pais && !it.paises.includes(user.pais)) return false;
    // Perfil (subrol): mismo criterio. Sirve para que «Módulos» lleve a cada
    // persona al curso que le toca sin tener que abrir un submenú.
    if (Array.isArray(it.subroles) && it.subroles.length && !it.subroles.includes(user?.subrol || '')) return false;
    // …y al revés: perfiles a los que este ítem se les esconde (p. ej. los
    // inactivos o graduados, que ya no entran a los cursos).
    if (Array.isArray(it.subroles_excluidos) && it.subroles_excluidos.includes(user?.subrol || '')) return false;
    return true;
  };

  const visibles = (items || []).filter(esVisible);

  // El menú personalizado reemplaza por completo la navegación por defecto del
  // header, que incluía el acceso al panel de administración. Lo reinyectamos
  // aquí para que los admins no lo pierdan en proyectos con menú propio (si el
  // menú ya define su propio enlace a /admin-panel, no se duplica).
  const yaTieneAdmin = visibles.some(it => (it.url || '').replace(/\/+$/, '').endsWith('/admin-panel'));
  const conAdmin = (user?.role === 'admin' && !yaTieneAdmin)
    ? [{ id: '__admin_panel', tipo: 'enlace', url: '/admin-panel', texto: 'Panel Admin' }, ...visibles]
    : visibles;
  // Tutor de entregas: se agrega solo «Entregas por revisar (n)» antes de «Salir»
  // (se puede apagar en la ficha: config_json.entregas.menu_auto = false).
  const salir = conAdmin.findIndex(it => it.tipo === 'logout');
  const itemEntregas = { id: '__entregas', tipo: 'enlace', url: '/entregas', texto: textoMenuEntregas(proyecto, entregasPendientes) };
  const finales = (esTutor(user) && proyecto?.config_json?.entregas?.menu_auto !== false && !conAdmin.some(it => it.url === '/entregas'))
    ? (salir >= 0 ? [...conAdmin.slice(0, salir), itemEntregas, ...conAdmin.slice(salir)] : [...conAdmin, itemEntregas])
    : conAdmin;

  if (finales.length === 0) return null;

  const hijosVisibles = (it) => (Array.isArray(it.hijos) ? it.hijos : []).filter(esVisible);
  // ¿El ítem navega a algún sitio por sí mismo? Un padre sin destino solo abre
  // su submenú; uno con destino navega y el submenú se abre con el hover / la
  // flechita (móvil).
  const tieneDestino = (it) => {
    if (it.tipo === 'enlace') return !!it.url;
    if (it.tipo === 'pagina') return !!it.pagina_slug;
    return ['home', 'contenidos', 'login', 'registro', 'logout'].includes(it.tipo);
  };

  // ¿El ítem apunta a la página en la que se está? Solo importa si el menú
  // define un estilo para el ítem activo (`fondo_activo` / `color_activo`).
  const sinBarra = (r) => (r.replace(/[?#].*$/, '').replace(/\/+$/, '') || '/');
  const apuntaAqui = (it) => {
    const actual = sinBarra(pathname);
    if (it.tipo === 'home') return actual === '/';
    if (it.tipo === 'contenidos') return actual === '/contenidos';
    if (it.tipo === 'pagina') return !!it.pagina_slug && actual === `/p/${it.pagina_slug}`;
    // Los enlaces a un ancla (#…) no marcan la página entera.
    if (it.tipo === 'enlace' && it.url && it.url.startsWith('/') && !it.url.includes('#')) {
      // Incluye sus subpáginas (/actualizacion/un-articulo), salvo la portada.
      const destino = sinBarra(it.url);
      return actual === destino || (destino !== '/' && actual.startsWith(`${destino}/`));
    }
    return false;
  };
  // Solo se marca el primero que coincide: si varios ítems llevan al mismo sitio
  // no se encienden todos a la vez.
  const idActivo = (activoBg || activoColor) ? (finales.find(apuntaAqui)?.id ?? null) : null;
  const esActivo = (it) => idActivo !== null && it.id === idActivo;

  const go = (it) => {
    setOpen(false);
    setSubAbierto(null);
    setSubMovil(null);
    switch (it.tipo) {
      case 'home': navigate('/'); break;
      case 'contenidos': navigate('/contenidos'); break;
      case 'pagina': navigate(`/p/${it.pagina_slug}`); break;
      case 'enlace':
        if (it.url) {
          if (/^https?:\/\//i.test(it.url)) {
            // Por defecto abrir en la MISMA pestaña. Solo abre en pestaña nueva
            // si el ítem lo pide explícitamente (it.nueva_pestana === true).
            if (it.nueva_pestana === true) window.open(it.url, '_blank', 'noopener');
            else window.location.href = it.url;
          } else {
            navigate(it.url);
          }
        }
        break;
      case 'login': onLogin ? onLogin() : navigate('/login'); break;
      case 'registro': onRegister ? onRegister() : navigate('/registro'); break;
      case 'logout': onLogout && onLogout(); break;
      default: break;
    }
  };

  // Los textos del menú pueden ser una cadena o un objeto {es, en, pt}: el menú
  // se traduce solo con el idioma activo (que en Uroacademy lo fija el país).
  const txt = (v) => {
    if (v == null) return '';
    if (typeof v === 'object') return v[idioma] || v.es || '';
    return v;
  };

  const renderItem = (it, opts = {}) => {
    // Color por ítem (override de esta página) > color único de la página > color global del ítem > acento.
    const activo = esActivo(it);
    const itemColor = (activo && activoColor) || (itemColors && itemColors[it.id]) || colorOverride || it.color_texto || accent;
    const bordeLado = it.borde_lado && it.borde_lado !== 'ninguno' ? it.borde_lado : null;
    const iconUrl = it.icono ? (it.icono.startsWith('http') ? it.icono : `${BACKEND_BASE}/${it.icono}`) : null;
    const soloIcono = it.ocultar_texto && iconUrl;
    const iconSize = it.pos_icono === 'top' ? 26 : 20;
    const iconH = it.icono_alto ? `${it.icono_alto}px` : `${iconSize}px`;
    const iconMaxW = it.icono_ancho ? `${it.icono_ancho}px` : '40px';
    return (
      <ItemBtn
        key={it.id}
        type="button"
        onClick={opts.onClick || (() => go(it))}
        $bg={(activo && activoBg) || it.color_fondo}
        $color={itemColor}
        $iconPos={it.pos_icono || 'left'}
        $size={fontSize}
        $peso={pesoMenu}
        $font={fontMenu}
        $bordeLado={bordeLado || undefined}
        $bordeAncho={it.borde_ancho}
        $bordeColor={it.borde_color}
        data-borde={bordeLado || undefined}
        $movilSize={movilFontSize}
        $movilColor={(activo && activoColor) || (itemColors && itemColors[it.id]) || movilColor || colorOverride}
        $hoverColor={activo ? (activoColor || undefined) : (it.color_hover || hoverColor)}
        $hoverBg={activo ? (activoBg || undefined) : (it.fondo_hover || hoverBg)}
        aria-current={activo ? 'page' : undefined}
        title={txt(it.texto)}
        style={opts.style}
        aria-haspopup={opts.conCaret ? 'true' : undefined}
        aria-expanded={opts.conCaret ? !!opts.caretAbierto : undefined}
      >
        {iconUrl && <img src={iconUrl} alt="" style={{ height: iconH, width: 'auto', maxWidth: iconMaxW }} />}
        {!soloIcono && <span>{txt(it.texto)}</span>}
        {opts.conCaret && (
          <span style={{ fontSize: '0.7em', marginLeft: 2, transition: 'transform 0.2s', display: 'inline-block', transform: opts.caretAbierto ? 'rotate(180deg)' : 'none' }}>▾</span>
        )}
      </ItemBtn>
    );
  };

  // Sub-ítem del desplegable de escritorio.
  const renderSubItem = (h) => {
    const iconUrl = h.icono ? (h.icono.startsWith('http') ? h.icono : `${BACKEND_BASE}/${h.icono}`) : null;
    return (
      <SubItemBtn
        key={h.id}
        type="button"
        onClick={() => go(h)}
        $color={subColor || '#0f355b'}
        $size={fontSize}
        $font={fontMenu}
        $hoverColor={hoverColor}
        $hoverBg={hoverBg}
        title={txt(h.texto)}
      >
        {iconUrl && <img src={iconUrl} alt="" style={{ height: 18, width: 'auto', maxWidth: 32 }} />}
        <span>{txt(h.texto)}</span>
      </SubItemBtn>
    );
  };

  // Escritorio: ítem simple o ítem con desplegable.
  const renderDesktop = (it) => {
    const hijos = hijosVisibles(it);
    if (hijos.length === 0) return renderItem(it);
    const abierto = subAbierto === it.id;
    const bordeLado = it.borde_lado && it.borde_lado !== 'ninguno' ? it.borde_lado : null;
    return (
      <SubWrap
        key={it.id}
        data-borde={bordeLado || undefined}
        onMouseEnter={() => setSubAbierto(it.id)}
        onMouseLeave={() => setSubAbierto(s => (s === it.id ? null : s))}
      >
        {renderItem(it, {
          conCaret: true,
          caretAbierto: abierto,
          onClick: () => (tieneDestino(it) ? go(it) : setSubAbierto(s => (s === it.id ? null : it.id))),
        })}
        <Dropdown $open={abierto} $pos={posicion} $bg={subBg}>
          {hijos.map(renderSubItem)}
        </Dropdown>
      </SubWrap>
    );
  };

  // Móvil (drawer): ítem simple o acordeón con sus hijos indentados.
  const renderMovil = (it) => {
    const hijos = hijosVisibles(it);
    if (hijos.length === 0) return renderItem(it);
    const abierto = subMovil === it.id;
    const colorFlecha = (itemColors && itemColors[it.id]) || movilColor || colorOverride || it.color_texto || accent;
    return (
      <SubWrap key={it.id}>
        <MobileRow>
          {renderItem(it, {
            style: { flex: 1 },
            onClick: () => (tieneDestino(it) ? go(it) : setSubMovil(s => (s === it.id ? null : it.id))),
          })}
          <Caret
            type="button"
            $open={abierto}
            $color={colorFlecha}
            aria-label={`Abrir submenú de ${txt(it.texto)}`}
            aria-expanded={abierto}
            onClick={() => setSubMovil(s => (s === it.id ? null : it.id))}
          >
            ▼
          </Caret>
        </MobileRow>
        <MobileSubList $open={abierto}>
          {hijos.map(h => renderItem(h, { style: { paddingLeft: '0.6rem', opacity: 0.95 } }))}
        </MobileSubList>
      </SubWrap>
    );
  };

  const cerrarDrawer = () => { setOpen(false); setSubMovil(null); };

  return (
    <Wrap $pos={posicion} style={{ position: 'relative' }}>
      <DesktopList $bg={bgMenu} $radius={radiusMenu} $pad={padMenu} $sep={sepMenu}>
        {finales.map(renderDesktop)}
      </DesktopList>
      <Burger $color={accent} onClick={() => (open ? cerrarDrawer() : setOpen(true))} aria-label="Menú">☰</Burger>
      <Drawer $open={open} $pos={posicion} $bg={movilBg} $ancho={movilAncho}>
        {finales.map(renderMovil)}
      </Drawer>
    </Wrap>
  );
};

export default CustomMenu;
